import React, { useEffect, useState } from "react";
import { Card, Badge, Button, LoadingState, ErrorState, EmptyState, GradientBar, PremiumBadge, Avatar } from "@/components/design-system";
import { getMyReputation, getReputationRules, getLeaderboard } from "@/lib/reputation";
import { formatApiError } from "@/lib/api";

const cn = (...xs) => xs.filter(Boolean).join(" ");

const KIND_LABEL = {
  submission: "Feedback submitted",
  helpful_marked: "Marked helpful",
  cited_in_report: "Cited in feedback report",
  submission_removed: "Feedback removed",
  submission_restored: "Feedback restored",
  manual_adjust: "Manual adjustment",
};

const TIER_ACCENT = {
  ambassador_eligible: { badge: "ambassador", label: "Ambassador" },
  validator: { badge: "active", label: "Validator" },
  explorer: { badge: "community", label: "Explorer" },
};

function TierBar({ summary }) {
  const totalToNext = summary.next_tier_at ? Math.max(1, summary.next_tier_at) : 1;
  const pct = summary.next_tier_at ? Math.min(100, Math.round((summary.points / totalToNext) * 100)) : 100;
  return (
    <div>
      <div className="flex items-center justify-between text-xs text-[#0B1F3A]/70">
        <span className="font-bold uppercase tracking-widest">{summary.tier_label}</span>
        {summary.next_tier_label ? (
          <span>{summary.to_next_tier} pts to <b className="text-[#0B1F3A]">{summary.next_tier_label}</b></span>
        ) : (
          <span className="font-bold uppercase tracking-widest text-[#7ED957]">Top tier</span>
        )}
      </div>
      <div className="mt-2 h-2.5 rounded-full bg-[#E8F1FF] overflow-hidden relative">
        <div className="absolute inset-0 flex">
          <div className="h-full bg-[#2D7FF9]" style={{ width: `${pct}%` }} />
        </div>
      </div>
    </div>
  );
}

// ---------- Leaderboard ---------------------------------------------------
const PODIUM = [
  { rank: 1, ring: "ring-[#F5B301]/70",   bg: "bg-gradient-to-b from-[#FFF6D6] to-white", medal: "1st", tint: "text-[#8A6600]" },
  { rank: 2, ring: "ring-[#B9C0CD]/70",   bg: "bg-gradient-to-b from-[#EEF1F6] to-white", medal: "2nd", tint: "text-[#4A5568]" },
  { rank: 3, ring: "ring-[#CD8B58]/70",   bg: "bg-gradient-to-b from-[#FDE7D3] to-white", medal: "3rd", tint: "text-[#7A4B23]" },
];

function PodiumCard({ row, meta }) {
  return (
    <div
      data-testid={`leaderboard-podium-${meta.rank}`}
      className={cn(
        "relative rounded-2xl border border-[#0B1F3A]/10 p-5 flex flex-col items-center text-center",
        "shadow-sm hover:shadow-md transition-shadow ring-2", meta.ring, meta.bg
      )}
    >
      <div className={cn("absolute top-3 left-3 text-[10px] font-black uppercase tracking-widest", meta.tint)}>{meta.medal}</div>
      <div className="mt-2">
        <Avatar name={row?.name || "—"} className="!w-16 !h-16 !text-base" />
      </div>
      <div className="mt-3 min-w-0 max-w-full">
        <div className="flex items-center justify-center gap-1.5 flex-wrap">
          <span className="text-sm font-bold text-[#0B1F3A] truncate max-w-[130px]">{row?.name || "—"}</span>
          {row?.plan === "premium" && <PremiumBadge withStar={false} label="Prem" />}
        </div>
        <div className="text-[10px] uppercase tracking-widest text-[#0B1F3A]/50 mt-0.5">
          {TIER_ACCENT[row?.tier]?.label || row?.tier || "Explorer"}
        </div>
      </div>
      <div className="mt-3 font-[Montserrat] font-extrabold text-3xl text-[#0B1F3A] tabular-nums">
        {row?.points ?? 0}
      </div>
      <div className="text-[10px] uppercase tracking-widest text-[#0B1F3A]/50">points</div>
    </div>
  );
}

function LeaderboardRow({ row, i, maxPoints, isMe }) {
  const pct = maxPoints > 0 ? Math.max(4, Math.round((row.points / maxPoints) * 100)) : 0;
  const tierMeta = TIER_ACCENT[row.tier] || { label: row.tier || "Explorer" };
  return (
    <li
      data-testid={`leaderboard-row-${i + 4}`}
      className={cn(
        "flex items-center gap-3 rounded-xl px-3 py-2.5 border transition-colors",
        isMe ? "border-[#2D7FF9]/60 bg-[#2D7FF9]/5" : "border-transparent hover:bg-[#E8F1FF]/40"
      )}
    >
      <span className="w-8 text-right text-[#0B1F3A]/55 font-bold tabular-nums text-sm">{i + 4}</span>
      <Avatar name={row.name || "—"} className="!w-8 !h-8 !text-[10px]" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="text-sm font-semibold text-[#0B1F3A] truncate">{row.name || "—"}</span>
          {row.plan === "premium" && <PremiumBadge withStar={false} label="Prem" />}
          {isMe && <span className="text-[9px] font-black uppercase tracking-widest text-[#2D7FF9]">You</span>}
        </div>
        <div className="mt-1 flex items-center gap-2">
          <div className="flex-1 h-1.5 rounded-full bg-[#E8F1FF] overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#20C7E8] to-[#2D7FF9]" style={{ width: `${pct}%` }} />
          </div>
          <span className="text-[10px] uppercase tracking-widest text-[#0B1F3A]/50">{tierMeta.label}</span>
        </div>
      </div>
      <div className="font-[Montserrat] font-extrabold text-lg text-[#0B1F3A] tabular-nums">{row.points}</div>
    </li>
  );
}

function LeaderboardSection({ board, meUserId }) {
  const top10 = board.slice(0, 10);
  const podium = top10.slice(0, 3);
  const rest = top10.slice(3);
  const maxPoints = top10[0]?.points || 1;

  if (top10.length === 0) {
    return (
      <Card data-testid="reputation-leaderboard-card" className="mt-8">
        <h2 className="font-[Montserrat] font-extrabold text-2xl text-[#0B1F3A]">Community leaderboard</h2>
        <div className="mt-4"><EmptyState title="Leaderboard is warming up" description="Reputation points earned by community members show up here." /></div>
      </Card>
    );
  }

  return (
    <Card data-testid="reputation-leaderboard-card" className="mt-8 !p-0 overflow-hidden">
      <div className="p-6 md:p-8 pb-2">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#2D7FF9]">Top of the community</p>
            <h2 className="font-[Montserrat] font-extrabold text-2xl md:text-3xl text-[#0B1F3A] mt-1">Community leaderboard</h2>
            <p className="text-sm text-[#0B1F3A]/65 mt-1">Premium members surface first, then ranked by points.</p>
          </div>
          <div className="text-[10px] uppercase tracking-widest text-[#0B1F3A]/55 font-bold">
            {top10.length} of top {board.length}
          </div>
        </div>
      </div>

      {/* Podium — top 3 */}
      <div className="px-6 md:px-8 pt-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="reputation-leaderboard-podium">
          {PODIUM.map((meta) => (
            <PodiumCard key={meta.rank} row={podium[meta.rank - 1]} meta={meta} />
          ))}
        </div>
      </div>

      {/* Ranked list — 4-10 */}
      {rest.length > 0 && (
        <div className="px-6 md:px-8 pt-6 pb-6">
          <ol className="space-y-1.5" data-testid="reputation-leaderboard">
            {rest.map((row, i) => (
              <LeaderboardRow key={row.user_id} row={row} i={i} maxPoints={maxPoints} isMe={row.user_id === meUserId} />
            ))}
          </ol>
        </div>
      )}
    </Card>
  );
}

// ---------- History (compact) ---------------------------------------------
function HistoryList({ events }) {
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? events : events.slice(0, 6);
  return (
    <Card data-testid="reputation-history-card">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-[Montserrat] font-bold text-lg text-[#0B1F3A]">Your history</h2>
        <span className="text-[10px] uppercase tracking-widest text-[#0B1F3A]/55 font-bold">{events.length} events</span>
      </div>
      {events.length === 0 ? (
        <div className="mt-4"><EmptyState title="No reputation events yet" description="Give structured feedback in an open campaign to start earning." /></div>
      ) : (
        <>
          <ul className="mt-3 divide-y divide-[#0B1F3A]/10" data-testid="reputation-events">
            {visible.map((e) => (
              <li key={e.id} className="py-2.5 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[13px] font-semibold text-[#0B1F3A] truncate">{KIND_LABEL[e.kind] || e.kind}</div>
                  <div className="text-[10px] uppercase tracking-widest text-[#0B1F3A]/50 mt-0.5">{new Date(e.created_at).toLocaleDateString()}</div>
                </div>
                <div className={cn("font-[Montserrat] font-extrabold text-base tabular-nums", e.delta >= 0 ? "text-[#2D7FF9]" : "text-red-600")}>
                  {e.delta >= 0 ? `+${e.delta}` : e.delta}
                </div>
              </li>
            ))}
          </ul>
          {events.length > 6 && (
            <div className="mt-3">
              <button
                type="button"
                onClick={() => setShowAll((v) => !v)}
                data-testid="reputation-history-toggle"
                className="text-xs font-bold uppercase tracking-widest text-[#2D7FF9] hover:text-[#0B1F3A]"
              >
                {showAll ? "Show less ▲" : `Show all ${events.length} events ▼`}
              </button>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

// ---------- How it works (compact rules) ----------------------------------
function HowItWorks({ rules }) {
  return (
    <Card data-testid="reputation-rules-card">
      <h3 className="font-[Montserrat] font-bold text-lg text-[#0B1F3A]">How it works</h3>
      <ul className="mt-3 space-y-2 text-sm" data-testid="reputation-rules">
        {rules.rules.map((r) => (
          <li key={r.kind} className="flex gap-3 text-[#0B1F3A]/85">
            <span className={cn("font-bold w-10 text-right tabular-nums", r.delta >= 0 ? "text-[#2D7FF9]" : "text-red-600")}>{r.delta >= 0 ? `+${r.delta}` : r.delta}</span>
            <span>{r.label}</span>
          </li>
        ))}
      </ul>
      <div className="mt-5 pt-5 border-t border-[#0B1F3A]/10 text-xs text-[#0B1F3A]/60 space-y-1">
        {rules.tiers.map((t) => (
          <div key={t.tier} className="flex justify-between"><span>{t.label}</span><b className="text-[#0B1F3A]">≥ {t.min_points} pts</b></div>
        ))}
      </div>
    </Card>
  );
}

// ---------- Page -----------------------------------------------------------
export default function Reputation() {
  const [state, setState] = useState({ status: "loading", data: null, rules: null, board: [], err: "" });

  const load = async () => {
    setState((s) => ({ ...s, status: "loading" }));
    try {
      const [me, rules, board] = await Promise.all([getMyReputation(), getReputationRules(), getLeaderboard()]);
      setState({ status: "ready", data: me, rules, board, err: "" });
    } catch (err) { setState({ status: "error", data: null, rules: null, board: [], err: formatApiError(err) }); }
  };
  useEffect(() => { load(); }, []);

  if (state.status === "loading") return <LoadingState label="Loading reputation…" />;
  if (state.status === "error") return <ErrorState detail={state.err} action={<Button variant="secondary" onClick={load}>Retry</Button>} />;

  const { summary, events } = state.data;
  const meUserId = summary?.user_id;

  return (
    <div data-testid="reputation-page">
      <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#2D7FF9]">Community</p>
      <h1 className="font-[Montserrat] font-extrabold text-3xl md:text-4xl tracking-tight text-[#0B1F3A] mt-2">Your reputation, in the open.</h1>
      <p className="text-[#0B1F3A]/70 mt-2 max-w-2xl">Every helpful action moves you closer to becoming a <b>Verified Ambassador</b>. The formula is transparent.</p>

      {/* Identity card */}
      <Card className="mt-8 !p-0 overflow-hidden">
        <GradientBar />
        <div className="p-6 md:p-8">
          <div className="flex items-center gap-4 flex-wrap">
            <Badge variant={summary.tier === "ambassador_eligible" ? "ambassador" : summary.tier === "validator" ? "active" : "community"} data-testid="my-tier">{summary.tier_label}</Badge>
            <div className="font-[Montserrat] font-extrabold text-4xl text-[#0B1F3A] tracking-tighter" data-testid="my-points">{summary.points}</div>
            <div className="text-xs text-[#0B1F3A]/60 uppercase tracking-widest">points</div>
          </div>
          <div className="mt-5"><TierBar summary={summary} /></div>
        </div>
      </Card>

      {/* Big, hero Leaderboard */}
      <LeaderboardSection board={state.board} meUserId={meUserId} />

      {/* History (left, compact) + How it works (bottom-right, compact) */}
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <HistoryList events={events} />
        </div>
        <div>
          <HowItWorks rules={state.rules} />
        </div>
      </div>
    </div>
  );
}
